import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useMutation } from "urql";
import { useLocation } from "react-router";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { FormProvider, useForm } from "react-hook-form";

import { MANAGE_ACCOUNT_MUTATION } from "../../graphql/mutations";
import { getString, handlePostRequest } from "../../utilities";
import { gtmSubscribed } from "../../utilities/gtm";
import { subscribeSubmit } from "../../utilities/subscribe";
import { userRoles, recaptchaSiteKey, tokenName, getLangCode, language, getParamValue } from "../../config";
import OptionallyDisplayed from "../global/optionallyDisplayed";
import LoadingAnim from "../global/loadingAnim";
import ErrorMessage from "../global/errorMessage";
import PasswordConfirmModal from "../global/passwordConfirmModal";
import PlanButton from "./planButton";
import PaymentFields from "../subscribe/paymentFields";
import FamilyPlans from "./familyPlans";
import ClassroomPlans from "./classroomPlans";
import SchoolPlans from "./schoolPlans";
import StudentFields from "../global/studentFields";
import { planIdSelector, roleSelector } from "../../redux/slices/loginSlice";
import { useLazyQuery } from "../../hooks";
import { RECAPTCHA_VERIFY_QUERY } from "../../graphql/queries";
import { mpPaymentSubmitted, mpSubStarted } from "../../utilities/mixpanel";
import { setItem } from "../../utilities/localStorage";

export default function ManageSubscriptionForm({
  accountBalance,
  plan,
  setPlan,
  planData,
  isPremiumWeb,
  isUpdating,
  setIsUpdating,
  success,
  accountInfo,
  setOriginalPlan,
  setOriginalAmount,
  queryErrors,
  isLegacy = false,
}) {
  const location = useLocation();
  const loginRole = useSelector(roleSelector);
  const planId = useSelector(planIdSelector);

  const [planAmount, setPlanAmount] = useState(null);
  const [role, setRole] = useState(loginRole);
  const [total, setTotal] = useState(0);
  const [passwordModal, setPasswordModal] = useState(false);
  const [submitErrors, setSubmitErrors] = useState(null);
  const [creditComplete, setCreditComplete] = useState(false);

  const isFree = loginRole === userRoles.free;
  const planChosen = !!plan;

  const showForm = (isFree || accountInfo?.canceled || isLegacy) && planChosen;

  const finalAmount = planAmount;

  const balanceGteAmount = accountBalance >= finalAmount;

  const freeCanUpdate = !isUpdating && showForm && (creditComplete || balanceGteAmount);

  const premiumCanUpdate =
    !isUpdating &&
    ((planChosen && !accountInfo?.canceled && planData?.[role]?.[plan]?.planId !== planId) ||
      (planChosen && accountInfo?.canceled));

  const canUpdate = isFree ? freeCanUpdate : premiumCanUpdate;

  const creditNotRequired =
    (planChosen && finalAmount < total) || (!isFree && accountInfo?.sourceExists) || balanceGteAmount;

  const formActionString = "Manage_Subscription_Payment_Form_Submission";

  const { getValues, handleSubmit, register, watch, reset, trigger, formState } = useForm();

  const stripe = useStripe();
  const elements = useElements();

  useEffect(() => {
    setRole(loginRole);
  }, [loginRole]);

  const onUpdateCompleted = (data) => {
    const upgradeRoleTitle = {
      FAMILY: getString(`subscribe.plans.family.title.4`),
      CLASSROOM: getString(`subscribe.plans.classroom.title.4`),
      SCHOOL: getString(`subscribe.plans.school.title.4`),
    };

    // default to free user upgrading message
    let successMessage = (
      <>
        <p>
          <strong>
            {getString("manage.subscription.success.free.upgraded", { replace: [upgradeRoleTitle[role]] })}
          </strong>
        </p>
        <p>{getString("manage.subscription.success.free.instructions")}</p>
      </>
    );
    if (isPremiumWeb) {
      successMessage = <p>{getString("manage.subscription.success.premium", { replace: [upgradeRoleTitle[role]] })}</p>;
    }

    const cardElement = elements.getElement(CardElement);

    if (cardElement) {
      cardElement.clear();
    }

    gtmSubscribed(planData[role][plan], { invoiceId: data?.updateUser?.invoiceId });
    mpSubStarted({
      startType: isFree ? "new" : "update",
      months: data?.updateUser?.subscription?.months,
      planGroup: data?.updateUser?.subscription?.planGroup,
      amount: data?.updateUser?.subscription?.amount,
      periodStart: data?.updateUser?.periodStart,
      periodEnd: data?.updateUser?.periodEnd,
      subStartDate: data?.updateUser?.subStartDate,
      pathname: location.pathname,
    });

    const {
      updateUser: {
        subscription: { planId, billingPlatform },
        token,
      },
    } = data;
    setItem("planId", planId);
    setItem("billingPlatform", billingPlatform);
    if (isPremiumWeb) {
      setItem(tokenName, token);
    }
    success({ message: successMessage, reload: true, ...(isPremiumWeb && { token }) });
  };

  const onError = (error) => {
    setSubmitErrors(error);
    setIsUpdating(false);
  };

  const [result, updateUser] = useMutation(MANAGE_ACCOUNT_MUTATION);
  const { fetching: mutating } = result;

  const onRecaptchaCompleted = async (data) => {
    const myVariables = {
      billingPlan: planData[role][plan].planId,
    };

    const formValues = getValues();
    if (formValues.accessName) {
      myVariables.accessName = formValues.accessName;
    }

    if (formValues.accessCode) {
      myVariables.accessCode = formValues.accessCode;
    }

    await subscribeSubmit({
      data,
      setSubmitErrors,
      cardElement: elements.getElement(CardElement),
      mutation: (variables) =>
        updateUser(variables).then((result) => handlePostRequest(result, onUpdateCompleted, onError)),
      creditNotRequired: true,
      setSubmitting: setIsUpdating,
      plan,
      myVariables,
      creditComplete,
      action: formActionString,
      stripe,
    });
  };

  const { executeQuery: recaptchaVerify, loading: verifying } = useLazyQuery(
    RECAPTCHA_VERIFY_QUERY,
    onRecaptchaCompleted,
    onError,
  );

  const loading = mutating || verifying;
  const submitForm = (creditNotRequired) => {
    setIsUpdating(true);

    // execute recaptcha to get token
    window.grecaptcha.enterprise.ready(async () => {
      const token = await window.grecaptcha.enterprise.execute(recaptchaSiteKey, { action: formActionString });

      if (token) {
        recaptchaVerify({ token, action: formActionString, verify: true, siteKey: recaptchaSiteKey });
      }
    });
  };

  const planSelectFunc = (roleString) => {
    setRole(roleString);
    setPlan(null);
    setPlanAmount(null);
    setOriginalPlan(null);
    setOriginalAmount(null);
    setTotal(0);
    reset();
  };

  useEffect(() => {
    if (planData) {
      // automate plan selection if valid query param
      const subscriptionParam = getParamValue("subscription", { location });
      const familyString = getString("family").toLowerCase();
      const classroomString = getString("classroom").toLowerCase();
      if ([familyString, classroomString].includes(subscriptionParam)) {
        const subscription = "m12";
        let data = planData.FAMILY;
        let role = userRoles.family;

        if (subscriptionParam === classroomString) {
          data = planData.CLASSROOM;
          role = userRoles.classroom;
        }
        planSelectFunc(role);
        setPlanAmount(data[subscription].amount);
        setPlan(subscription);
      }
    }
  }, [planData]);

  const renderForm = () => {
    const subPlans = {
      FAMILY: <FamilyPlans plan={plan} setPlan={setPlan} setPlanAmount={setPlanAmount} planData={planData.FAMILY} />,
      CLASSROOM: (
        <ClassroomPlans plan={plan} setPlan={setPlan} setPlanAmount={setPlanAmount} planData={planData.CLASSROOM} />
      ),
      SCHOOL: <SchoolPlans plan={plan} setPlan={setPlan} setPlanAmount={setPlanAmount} planData={planData.SCHOOL} />,
    };

    let confirmTitle = "";

    if (role && role !== userRoles.free) {
      confirmTitle = getString(`subscribe.plans.${role.toLowerCase()}.title.${role === userRoles.school ? 1 : 2}`);
    }

    const isStudentPlan = [userRoles.classroom, userRoles.school].includes(role);

    return (
      <>
        {subPlans[role] || null}

        <form
          onSubmit={handleSubmit(() => {
            if (isFree) {
              submitForm(canUpdate && creditNotRequired);
            } else {
              setPasswordModal(true);
            }
            // Mixpanel Event
            mpPaymentSubmitted({
              months: plan,
              planGroup: role,
              amount: planAmount,
              finalAmount,
              pathname: location.pathname,
            });
          })}
        >
          <div className="container-small">
            <OptionallyDisplayed doDisplay={!!submitErrors}>
              <ErrorMessage error={submitErrors} />
            </OptionallyDisplayed>

            <OptionallyDisplayed doDisplay={showForm}>
              <FormProvider
                getValues={getValues}
                register={register}
                trigger={trigger}
                watch={watch}
                reset={reset}
                formState={formState}
              >
                <OptionallyDisplayed doDisplay={isStudentPlan}>
                  <StudentFields submitErrors={submitErrors} setSubmitErrors={setSubmitErrors} />
                </OptionallyDisplayed>
                <PaymentFields
                  doesNotRequireCredit={creditNotRequired}
                  setCreditComplete={setCreditComplete}
                  submitErrors={submitErrors}
                  setSubmitErrors={setSubmitErrors}
                />
              </FormProvider>
            </OptionallyDisplayed>

            <div className="manage-form-footer">
              <button type="submit" className="button-flat-color pt-green manage-update-button" disabled={!canUpdate}>
                {getString("update.0")}
              </button>

              <OptionallyDisplayed doDisplay={showForm}>
                <div data-testid="subscribe-terms-id" className="subscribe-terms">
                  <p>{getString("subscribe.terms", { replace: [getString("update.3")], html: true })}</p>
                  <p>{getString("recaptcha", { html: true })}</p>
                </div>
              </OptionallyDisplayed>
            </div>
          </div>
        </form>

        <PasswordConfirmModal
          doShow={!!passwordModal}
          close={() => {
            setPasswordModal((prev) => !prev);
          }}
          submit={() => {
            if (!isUpdating) {
              setPasswordModal((prev) => !prev);
              submitForm(canUpdate && creditNotRequired);
            }
          }}
        >
          <p>{getString("manage.password")}</p>
        </PasswordConfirmModal>

        {(loading || isUpdating) && (
          <LoadingAnim position="fixed" className="background-transparent-white">
            <h4>{getString("update.1")}</h4>
          </LoadingAnim>
        )}
      </>
    );
  };

  if (queryErrors) {
    return (
      <div className="container-small">
        <ErrorMessage error={queryErrors} />
      </div>
    );
  }
  if (planData) {
    return (
      <>
        <OptionallyDisplayed doDisplay={isFree}>
          {/* plan selection only available to free/public subscribers */}
          <div className="cta">{getString("subscribe.cta")}</div>
          <div className="planselect">
            <PlanButton plan={role} pRole={userRoles.family} color="pt-cyan" func={planSelectFunc} />
            <PlanButton plan={role} pRole={userRoles.classroom} color="pt-blue" func={planSelectFunc} />
            {getLangCode() === language.default && (
              <PlanButton plan={role} pRole={userRoles.school} color="school-sub" func={planSelectFunc} />
            )}
          </div>
        </OptionallyDisplayed>

        {renderForm()}
      </>
    );
  }

  return <LoadingAnim />;
}
